import {Subject} from "rxjs";
import {Item} from "./Item";
import {TranscriptionItem} from "./TranscriptionItem";
import {LanguagePipe} from "../../app/pipe/language.pipe";
import {ProductItem} from "./ProductItem";
import {Product} from "../Product";
import {Draft} from "../Draft";
import {DraftItem} from "./DraftItem";
import {OrderOption} from "./OrderOption";
import {PackageAndShippingComputationService} from "../../app/service-data/package-and-shipping-computation.service";
import {Address} from "../Address";
import {Organization} from "../Organization";
import {ChargeProfile} from "../ChargeProfile";
import {Facility} from "../Facility";
import {isDiscontinuedBindingType} from "../BindingType";
import {SalesTaxRateResult} from "../../app/service-data/sales-tax.service";

/** Stage of the cart workflow. */
export type CartStage = 'CART' | 'CHECKOUT' | 'CONFIRMATION';
export type ItemsStatus = 'EMPTY' | 'READY';
export type AddressStatus = 'PENDING' | 'ENTERED' | 'VALIDATING' | 'VALIDATED' | 'MANUAL_OVERRIDE';
export type ComputationStatus = 'PENDING' | 'COMPUTING' | 'READY' | 'ERROR';
export type PaymentStatus = 'PENDING' | 'READY';
export type PlaceOrderStatus = 'IDLE' | 'PLACING' | 'SUCCESS' | 'ERROR';

/**
 * A single carrier + service-level offering the user can pick from
 * the shipping section of the checkout, produced by flattening every
 * `OrderOption` → `PrintNodeOrderOption` → `ShippingPackage` →
 * `ShippingOption` across the cart's computed shipping quotes.
 *
 * Each `(carrier, serviceLevel)` combination the server has quoted
 * anywhere in the cart appears exactly once. `orderOption` points at
 * the underlying package layout (ANY/ANY, USPS/PRIORITY_FLAT, etc.)
 * that produces the cheapest total for THIS combination — different
 * choices may point at different backing OrderOptions in the same
 * unified list. When the user picks a choice, both `orderOption`
 * (drives the packages) and `(carrier, serviceLevel)` (drives the
 * per-package rate lookup) are captured on the cart.
 */
export interface UnifiedShippingChoice {
  carrier: string;
  serviceLevel: string;
  shippingCost: number;
  packingCost: number;
  totalCost: number;
  orderOption: OrderOption;
  packageSummary: string;
  /**
   * Expected transit-time range in business days, computed by taking
   * the slowest package in the backing layout for this
   * `(carrier, serviceLevel)`. `null` when the server did not supply
   * a range for one or more of the underlying rate rows — the picker
   * suppresses the "N–M days" line in that case rather than showing
   * a partial estimate.
   *
   * Populated from `ShippingOption.deliveryDaysMin` /
   * `ShippingOption.deliveryDaysMax` (or the legacy single
   * `deliveryDays` promoted to a same-day range via
   * `ShippingOption.getDeliveryDaysRange`).
   */
  deliveryDaysMin?: number;
  deliveryDaysMax?: number;
}

export class Cart {
  userId: string;
  key: string;
  expeditedManufacturing: string;
  expeditedManufacturingCost: number;
  shippingMethod: string;
  shippingCost: number;
  transcriptionCost: number;
  itemsCost: number = 0;
  packingAndPackagingCost: number = 0;
  taxCost: number = 0;
  accountId: number;
  verificationKey: string;
  address: Address = new Address(undefined);
  packItemsInSeparateBoxes: boolean;
  useCaseQuantities: boolean;
  chargeProfile: ChargeProfile;
  invoiceable: boolean = false;
  organization: Organization;

  purchaseOrder: string;
  purchaseOrderPrivate: string;
  phone: string;
  phoneCountry: string;
  cardNumber: string;
  cardNumberWithSpaces: string;
  csc: string;
  expMonth: number;
  expYear: number;
  expMonthYear: string;
  firstNamePayment: string;
  lastNamePayment: string;
  address1Payment: string;
  address2Payment: string;
  cityPayment: string;
  statePayment: string;
  postalCodePayment: string;
  countryPayment: string;
  language: string;
  paymentErrorMessage: string = "";
  costsAndShippingErrorMessage: string = "";
  deliveryAddressErrorMessage: string = "";
  shippingErrorMessage: string = "";
  weight: number = 0;
  totalQuantity: number;
  totalWeight: number;
  totalCost: number;

  itemsInCart: number = 0;
  inventoryType = 'PRINT_TO_ORDER'; // 'PRINT_TO_ORDER';
  payer = 'ORGANIZATION';

  orderOptions: OrderOption[] = [];
  selectedOrderOption: OrderOption;

  /**
   * Fingerprint of the delivery address the current `orderOptions[]`
   * tree was computed against (see {@link computeAddressFingerprint}).
   *
   * The checkout address form mutates `cart.address` IN PLACE through
   * `[(ngModel)]`, and `applyValidatedAddress()` overwrites the street /
   * city / postal code with the carrier-validated version — neither goes
   * through `setAddress()`, so neither trips the invalidation there. Yet
   * `Cart.compute()` may already have run against the half-typed address
   * (`setAddress(undefined)` fires one the moment the user clicks "Add
   * address"), and the chosen facility + rates depend on the postal code.
   * Recording the fingerprint lets `isShippingStaleForAddress()` tell
   * "these rates are for a different destination" apart from "these rates
   * are current", so the auto-compute in `CartPageComponent` re-fires
   * after validation instead of keeping quotes for the old ZIP.
   */
  shippingAddressFingerprint: string = '';

  // ---- Workflow state ---------------------------------------------------
  /** Current stage of the cart workflow (single source of truth). */
  cartStage: CartStage = 'CART';
  itemsStatus: ItemsStatus = 'EMPTY';
  addressStatus: AddressStatus = 'PENDING';
  facilityStatus: ComputationStatus = 'PENDING';
  packagesStatus: ComputationStatus = 'PENDING';
  shippingStatus: ComputationStatus | string = 'PENDING';
  salesTaxStatus: ComputationStatus = 'PENDING';
  paymentStatus: PaymentStatus = 'PENDING';
  placeOrderStatus: PlaceOrderStatus = 'IDLE';

  /** Carrier/service-level the user has selected — preserved across CART<->CHECKOUT. */
  selectedCarrier: string = '';
  selectedServiceLevel: string = '';

  salesTaxResult: SalesTaxRateResult;
  /**
   * Opaque server-issued signature covering the most recent `salestax/rate`
   * call's inputs + outputs. Echoed in the place-order envelope under
   * `signatures.salesTax` so the server can skip the duplicate TaxJar call.
   * See `docs/contracts/outbox/2026-06-24-salestax-signature.md`.
   */
  salesTaxSignature: string = '';
  /**
   * Fingerprint of the delivery address as of the last `salestax/rate` call.
   * Used by the exempt-fast-path in `CartPageComponent.maybeComputeSalesTax`:
   * when the most recent tax response came back exempt AND this fingerprint
   * still matches the current `address`, production/shipping cost changes
   * alone do not force a recompute. Any address change clears this field
   * (via `setAddress`) so the next call is unconditionally issued.
   */
  salesTaxAddressFingerprint: string = '';

  /**
   * Most recent `order/getfacilities` response (server work unit S-2).
   * Populated when `environment.useNewCartShippingFlow` is true; empty
   * otherwise. Read by the cart-page to drive the facility radio group.
   * See `docs/contracts/shared/GetFacilities.md`.
   */
  facilityCandidates: Array<{
    facilityId: number;
    friendlyName: string;
    canProduceAll: boolean;
    splitRequired: boolean;
    assignments: Array<{ lineIndex: number; productId: number; canProduce: boolean; reasons: string[] }>;
  }> = [];
  /** Opaque signature covering the `facilityCandidates` payload. Echoed into `rateShipping` and place-order. */
  facilitySignature: string = '';
  /**
   * True when no single facility in `facilityCandidates` can produce every
   * item in the cart. The user must either pick a different shipping
   * destination, drop items, or (eventually) opt into a split order.
   */
  splitOrderRequired: boolean = false;

  /**
   * Most recent `order/rateshipping` response (server work unit S-3),
   * keyed by facilityId for quick lookup. Populated when
   * `useNewCartShippingFlow` is true. See `docs/contracts/shared/RateShipping.md`.
   */
  rateShippingByFacility: { [facilityId: number]: {
    packageSets: Array<{
      packageSetId: string;
      appliesTo: Array<{ carrier: string; serviceLevel: string }>;
      packages: Array<{ packageIndex: number; packageTypeId: number; width: number; height: number; depth: number; weight: number; itemsPerPackage: number; packingCost: number }>;
    }>;
    // `deliveryDaysMin` / `deliveryDaysMax` are optional: server-side
    // rate rows populated pre-2026-07-08 lack them (see the outbox
    // request `2026-07-08-shippingoption-transit-days.md`). Consumers
    // must tolerate missing values and either suppress the transit
    // line or synthesise a same-day range from a legacy single value.
    rates: Array<{ carrier: string; serviceLevel: string; packageSetId: string; totalCost: number; perPackageCosts: number[]; signature: string; deliveryDaysMin?: number; deliveryDaysMax?: number }>;
  }; } = {};
  /** Response-level signature covering the entire `rateShipping` payload. */
  rateShippingSignature: string = '';
  /**
   * The single rate row the user (or auto-select) has chosen for the
   * selected facility. Carries the per-rate signature that will be echoed
   * in the place-order envelope.
   */
  selectedShippingRateSignature: string = '';
  /** packageSetId of the selected shipping rate (for resolving `perPackageCosts` back to packages). */
  selectedPackageSetId: string = '';

  facilities: Facility[] = [];
  currentPackageCollectionNumber = 1;
  computationStatus: string = 'INITIAL';
  packageCollections: any = {};

  public items: Item[];
  public nonZeroItems: Item[];
//  public transcriptionItems: TranscriptionItem[];
  addressValidationStatus: string = 'INITIAL';
  packageAndShippingState: string = 'INITIAL';
  private packageAndShippingComputationService: PackageAndShippingComputationService;

  addressReady: boolean = false;
  shippingReady: boolean = false;
  paymentInfoReady: boolean = false;
  readyForInvoiceCheckout: boolean = false;
  readyForCreditCardCheckout: boolean = false;

  constructor (obj, packageAndShippingComputationService: PackageAndShippingComputationService, organization: Organization) {
    this.userId = '';
    this.key = '';
    this.expeditedManufacturing = '';
    this.expeditedManufacturingCost = 0;
    this.shippingMethod = '';
    this.shippingCost = 0;
    this.transcriptionCost = 0;
    this.itemsCost = 0;
    this.taxCost = 0;
    this.accountId = 0;
    this.verificationKey = '';
    this.phone = '';
    this.phoneCountry = '';
    this.cardNumber = '';
    this.csc = '';
    this.expMonth = 12;
    this.expYear = 2020;
    this.firstNamePayment = '';
    this.lastNamePayment = '';
    this.address1Payment = '';
    this.address2Payment = '';
    this.cityPayment = '';
    this.statePayment = '';
    this.postalCodePayment = '';
    this.countryPayment = 'US';
    this.language = 'en';
    this.paymentErrorMessage = "";
    this.costsAndShippingErrorMessage = "";
    this.deliveryAddressErrorMessage = "";
    this.shippingErrorMessage = "";
    this.packageAndShippingState = 'INITIAL';
    this.packItemsInSeparateBoxes = false;
    this.useCaseQuantities = false;

    this.organization = organization;
    if ( this.organization ) {
      if ( this.organization.canCheckOutWithInvoicePayment === 'TRUE' && this.organization.hasActiveTab === 'TRUE') {
        this.invoiceable = true;
      } else {
        this.invoiceable = false;
      }
    }

    if (obj) {
      for (const prop of Object.keys(obj)) {
        this[prop] = obj[prop];
      }
    }

    this.items = [];
    this.nonZeroItems = [];
    // if (obj) {
    //   if (obj.items) {
    //     for (const o of obj.items) {
    //       const item = new ProjectItem(o);
    //       this.items.push(item);
    //     }
    //   }
    // } else {
    // }
    this.packageAndShippingComputationService = packageAndShippingComputationService;
  }

  setNonZeroItems() {
    this.nonZeroItems = [];
    for ( const item of this.items ) {
      if ( item.quantity > 0 ) {
        this.nonZeroItems.push(item);
      }
    }
  }

  /**
   * Canonical Organization fulfillment defaults: the Organization pays
   * for its own order, and the order is printed to order.
   *
   * The "Payee" / "Inventory Type" selectors in checkout are only shown
   * to users who are also a ServiceProvider or a Network (see
   * `StartupService.canChooseFulfillmentOptions()`). Everyone else must
   * end up on these values — a cart restored from a previous session, or
   * one left behind by a service-provider operator who then switched
   * roles, could otherwise carry a payer / inventory type the current
   * user can neither see nor change, and it would be silently sent to
   * `placeOrder`. Callers invoke this whenever the cart is (re)bound to a
   * user or role.
   *
   * Returns true when something actually changed, so callers can decide
   * whether downstream state needs invalidating / logging.
   */
  applyDefaultFulfillmentOptions(): boolean {
    let changed = false;
    if ( this.payer !== 'ORGANIZATION' ) {
      this.payer = 'ORGANIZATION';
      changed = true;
    }
    if ( this.inventoryType !== 'PRINT_TO_ORDER' ) {
      this.inventoryType = 'PRINT_TO_ORDER';
      changed = true;
    }
    return changed;
  }
  // setOrganization(organziation: Organization) {
  //   this.organization = organziation;
  //   if ( this.organization ) {
  //     if ( this.organization.) {
  //       this.invoicable = true;
  //     } else {
  //       this.invoicable = true;
  //     }
  //   }
  // }
  /** Update the cart's organization and refresh derived flags (e.g. invoiceable). */
  setOrganization(org: Organization | null | undefined) {
    this.organization = org;
    if (this.organization) {
      const truthy = (v: any) => v === true || (typeof v === 'string' && v.trim().toLowerCase() === 'true');
      this.invoiceable = truthy(this.organization.canCheckOutWithInvoicePayment) && truthy(this.organization.hasActiveTab);
    } else {
      this.invoiceable = false;
    }
  }

  setChargeProfile(chargeProfile: ChargeProfile | null | undefined) {
    this.chargeProfile = chargeProfile || undefined;
  }

  setAddress(address: Address) {
    if ( !address ) {
      address = new Address(undefined);
    }
    // Address change must always invalidate sales tax, even if previously exempt.
    // Per docs/architecture/order-placement.md §3 recompute-trigger matrix.
    if (this.computeAddressFingerprint(address) !== this.salesTaxAddressFingerprint) {
      this.salesTaxResult = undefined;
      this.salesTaxSignature = '';
      this.salesTaxAddressFingerprint = '';
      this.salesTaxStatus = 'PENDING';
      // Address change also invalidates the facility-candidate set + the
      // shipping rates (both server endpoints take the address as input).
      this.facilityCandidates = [];
      this.facilitySignature = '';
      this.splitOrderRequired = false;
      this.rateShippingByFacility = {};
      this.rateShippingSignature = '';
      this.selectedShippingRateSignature = '';
      this.selectedPackageSetId = '';
      this.facilityStatus = 'PENDING';
      this.shippingStatus = 'PENDING';
    }
    this.address = address;
    if ( address.validated ) {
      this.addressValidationStatus = 'VALIDATED';
      this.addressStatus = 'VALIDATED';
    } else if ( address.override ) {
      this.addressValidationStatus = 'MANUAL_OVERRIDE';
      this.addressStatus = 'MANUAL_OVERRIDE';
    } else if ( address.ID > 0 ) {
      // Saved address from the address book — treat as pre-approved.
      this.addressValidationStatus = 'MANUAL_OVERRIDE';
      this.addressStatus = 'MANUAL_OVERRIDE';
    } else {
      this.addressStatus = 'ENTERED';
    }
    this.compute();
  }

  /**
   * Canonical fingerprint of a delivery address used to detect when the cart's
   * destination has changed in a way that forces a sales-tax recompute. Same
   * convention the server will use for the `addressFingerprint` field that
   * feeds into the salestax-rate response signature
   * (see `docs/contracts/outbox/2026-06-24-salestax-signature.md`).
   */
  computeAddressFingerprint(address: Address): string {
    if (!address) { return ''; }
    const trim = (s: string) => (s || '').trim().toLowerCase();
    const country = trim(address.country) === 'usa' ? 'us' : trim(address.country);
    return [
      trim(address.address1),
      trim(address.address2),
      trim(address.city),
      trim(address.state),
      trim(address.postalcode),
      country
    ].join('|');
  }

  /**
   * True when the cached shipping tree was computed against a DIFFERENT
   * delivery address than the cart currently carries — i.e. the quotes on
   * screen are for the wrong destination and must be recomputed. See
   * {@link shippingAddressFingerprint} for why in-place address edits
   * make this necessary.
   */
  isShippingStaleForAddress(): boolean {
    return this.computeAddressFingerprint(this.address) !== this.shippingAddressFingerprint;
  }

  /**
   * Refresh `itemsStatus` based on whether any item has a non-zero quantity.
   * Call after any quantity change.
   */
  recomputeItemsStatus(): ItemsStatus {
    for (const item of this.items) {
      if (item.quantity > 0) {
        this.itemsStatus = 'READY';
        return this.itemsStatus;
      }
    }
    this.itemsStatus = 'EMPTY';
    return this.itemsStatus;
  }

  /**
   * True when EVERY non-zero item in the cart has a numeric price
   * ready to charge (`Item.getPricingState() === 'READY'`). Consumed
   * by the toolbar Checkout button's disabled binding and by the
   * summary-bar hint bar. Zero-quantity items never block; a cart
   * with only zero-quantity items returns `false` (nothing to buy).
   *
   * Uses `getPricingState()` when the item exposes it (all current
   * ProductItem / DraftItem instances do); falls back to a
   * status-only heuristic for any legacy Item subclass that predates
   * the helper — `READY` + non-zero `cost` counts as priced.
   */
  allNonZeroItemsPriced(): boolean {
    let hasAnyNonZero = false;
    for (const item of this.items) {
      if (!item || item.quantity <= 0) { continue; }
      hasAnyNonZero = true;
      const any = item as any;
      if (typeof any.getPricingState === 'function') {
        if (any.getPricingState() !== 'READY') { return false; }
      } else {
        // Legacy fallback (no Item subclass currently hits this — kept
        // defensive so a stray older instance doesn't accidentally
        // gate checkout closed on itself).
        if (any.productionRatesStatus !== 'READY') { return false; }
      }
    }
    return hasAnyNonZero;
  }

  /**
   * Mark all quantity-derived computations as PENDING. Does NOT touch
   * address or payment selections — those persist across CART <-> CHECKOUT
   * transitions and are only invalidated when the user explicitly changes
   * the relevant inputs.
   *
   * Note: sales-tax invalidation here is *conditional*. Per
   * `docs/architecture/order-placement.md` §3, when the most recent tax
   * response came back exempt (or zero) AND the delivery address hasn't
   * changed, production/shipping cost changes alone do not force a
   * recompute. The actual "should I call again?" decision lives in
   * `CartPageComponent.maybeComputeSalesTax()`; here we keep the status
   * at PENDING so the UI shows "pending" until that decision is made.
   */
  invalidateDownstream() {
    this.facilityStatus = 'PENDING';
    this.packagesStatus = 'PENDING';
    this.shippingStatus = 'PENDING';
    this.salesTaxStatus = 'PENDING';
    // Quantity / item-set changes invalidate the facility candidates AND the
    // rate-shipping payload: server signature inputs include the item list,
    // so cached responses become stale the moment an item count moves.
    this.facilityCandidates = [];
    this.facilitySignature = '';
    this.splitOrderRequired = false;
    this.rateShippingByFacility = {};
    this.rateShippingSignature = '';
    this.selectedShippingRateSignature = '';
    this.selectedPackageSetId = '';
    // Also drop the user's previously-picked unified shipping choice.
    // The old `selectedOrderOption` points into the pre-invalidation
    // `orderOptions[]` tree whose packages were computed against a stale
    // item set; leaving it selected causes the shipping section to keep
    // showing the old carrier / cost while a re-compute is pending
    // (bug reported 2026-07-08: "shipping options I had selected with a
    // the earlier quantities was still presented as options"). Clearing
    // these fields flips `shippingReady` off in the parent's
    // `verifyCostsAndShipping` and forces the user to re-pick once the
    // fresh rates land.
    this.selectedOrderOption = undefined;
    this.selectedCarrier = '';
    this.selectedServiceLevel = '';
    this.shippingCost = 0;
    // Packing is tracked separately from shipping on the cart so the
    // order summary can list them on their own lines (some tax
    // jurisdictions treat packing / handling differently from
    // shipping — see 2026-07-09 pass 3 fix). Reset here alongside the
    // shipping fields so the "Packing and Packaging" summary row
    // doesn't stick to a stale value after a quantity edit.
    this.packingAndPackagingCost = 0;
    // Mark every PrintNodeOrderOption's cached package layout dirty so
    // `isShippingComputing` sees "still working" state, and any code
    // path that walks `orderOptions[]` before the imminent `compute()`
    // rebuild sees the packages as recomputing rather than READY.
    // Cheap; the whole `orderOptions[]` tree is about to be replaced by
    // `Cart.compute()` anyway.
    for (const oo of (this.orderOptions || [])) {
      for (const pnoo of (oo.printNodeOrderOptions || [])) {
        pnoo.computationStatus = 'DIRTY';
        for (const pkg of (pnoo.shippingPackages || [])) {
          pkg.computationStatus = 'DIRTY';
        }
      }
    }
    // Do NOT clear salesTaxResult / salesTaxSignature / salesTaxAddressFingerprint here.
    // The exempt-fast-path in maybeComputeSalesTax inspects salesTaxResult to decide
    // whether to short-circuit. Address change is the only event that wipes those.
  }

  // computeCosts(orderOption: OrderOption) {
  //   for ( const printNodeOrderOption of orderOption.printNodeOrderOptions ) {
  //     printNodeOrderOption.getShippingItems();
  //     this.packageAndShippingComputationService.getPackages("ANY", "ANY", printNodeOrderOption.facility, printNodeOrderOption.getShippingItems(), 1).subscribe( packages => {
  //       orderOption.state = 'READY';
  //
  //     });
  //   }
  // }

  setOrderOption(orderOption: OrderOption) {
    this.selectedOrderOption = orderOption;
  }

  /**
   * Adopt a unified shipping choice: pick the backing package layout
   * (via `selectedOrderOption`) AND record which per-package rate line
   * the user chose (via `selectedCarrier` / `selectedServiceLevel` and
   * `shippingCost`). Downstream consumers — the shipping-section
   * summary, `placeOrder` envelope construction, sales-tax recompute
   * — read those fields to know what shipping the user committed to.
   *
   * Called from the shipping-section radio group after
   * {@link getUnifiedShippingChoices} flattened the raw
   * `orderOptions[]` tree into one row per `(carrier, serviceLevel)`.
   */
  setSelectedShippingChoice(choice: UnifiedShippingChoice | null) {
    if (!choice) {
      this.selectedOrderOption = undefined;
      this.selectedCarrier = '';
      this.selectedServiceLevel = '';
      this.shippingCost = 0;
      this.packingAndPackagingCost = 0;
      try { this.selectedShippingChoiceNotifier.next(); } catch (e) { console.error('selectedShippingChoiceNotifier emit failed', e); }
      return;
    }
    this.selectedOrderOption = choice.orderOption;
    this.selectedCarrier = choice.carrier;
    this.selectedServiceLevel = choice.serviceLevel;
    // Split the two costs onto the two dedicated cart fields so the
    // order summary can render them on separate lines (per 2026-07-09
    // pass 3 request — some tax jurisdictions treat packing /
    // handling as taxable while shipping is exempt, or vice versa,
    // and `SalesTaxService.getRate(...)` already accepts them as
    // separate `packing` + `shipping` arguments). Previously
    // `shippingCost` carried the combined total and
    // `packingAndPackagingCost` stayed at zero, so the summary row
    // for packing always read "$0.00" and tax jurisdictions couldn't
    // differentiate.
    //
    // The combined value (what the picker's accordion header shows)
    // is trivially `shippingCost + packingAndPackagingCost` — the
    // header binding sums them at display time. Consumers that want
    // "the whole shipping-related charge" should sum the two fields
    // rather than reaching for `shippingCost` alone.
    this.shippingCost = choice.shippingCost;
    this.packingAndPackagingCost = choice.packingCost;
    // Notify listeners (cart-page.maybeComputeSalesTax) that the
    // selection changed. Fires for both manual picks and the
    // deferred-microtask auto-select in
    // `CartShippingSectionComponent.maybeAutoSelectChoice`. Combined
    // with the `shippingFullyComputedNotifier` fire in
    // `checkComputationStatus`, this covers both orderings:
    //   - Rates all land, THEN auto-select selects  → covered by
    //     this notifier (fully-computed notifier already fired
    //     harmlessly with a null selection).
    //   - Auto-select selects EARLY (only the picked pair has all
    //     its packages rated), THEN more rates land  → covered by
    //     the fully-computed notifier landing later, at which point
    //     `isSelectedShippingFullyComputed()` finally returns true.
    try { this.selectedShippingChoiceNotifier.next(); } catch (e) { console.error('selectedShippingChoiceNotifier emit failed', e); }
  }

  /**
   * Flatten the two-dimensional `(OrderOption × ShippingOption)` tree
   * into a one-dimensional list of pickable carrier + service-level
   * offerings.
   *
   * Motivation: `Cart.compute()` creates one `OrderOption` per package
   * layout constraint — e.g. an ANY/ANY OrderOption whose server-
   * generated packages accept most carriers, and a USPS/PRIORITY_FLAT
   * OrderOption whose packages are the specific flat-rate box.
   * Presenting these as an either/or radio conflates two orthogonal
   * dimensions: which BOX the order ships in vs which CARRIER+SERVICE
   * ships it. Users care about the latter (they see "USPS Priority",
   * "UPS Ground", "USPS Priority Flat Rate", etc. with prices); the
   * box choice is an implementation detail that should follow from
   * the carrier+service they pick.
   *
   * Algorithm — direct walk of the raw shipping-package tree so that
   * partially-computed OrderOptions still surface any (carrier,
   * serviceLevel) pair for which every package has a rate. Does NOT
   * gate on `PrintNodeOrderOption.computationStatus === 'COMPLETE'`
   * — that check would suppress every choice during the window
   * between the first `getPackages` response (which flips
   * `cart.shippingStatus = 'READY'`) and the final per-package
   * `getShippingOptions` response landing, which was the reason no
   * choices appeared in the initial rollout.
   *
   *  1. Walk every `orderOption` → `printNodeOrderOption` →
   *     `shippingPackage` → `shippingOption` and collect every
   *     `(carrier, serviceLevel)` pair the server has quoted.
   *  2. For each pair, evaluate its total cost against every
   *     OrderOption: for each PNOO in the layout, every package must
   *     have at least one `ShippingOption` matching `(carrier,
   *     serviceLevel)` — otherwise the layout can't ship the whole
   *     order via that pair (one of its physical boxes would have no
   *     rate). Multiply per-package rate × package `quantity` to
   *     match `ShippingPackage.getCost` semantics.
   *  3. Among eligible OrderOptions pick the one with the lowest
   *     total; that becomes the backing layout for the choice.
   *  4. Sort the resulting list by total cost ascending so the
   *     cheapest option surfaces first.
   */
  getUnifiedShippingChoices(): UnifiedShippingChoice[] {
    if (!this.orderOptions || this.orderOptions.length === 0) { return []; }
    // Step 1 — collect the (carrier, serviceLevel) universe.
    const pairKey = (c: string, s: string) => `${c}\u0000${s}`;
    const pairSet = new Map<string, { carrier: string; serviceLevel: string }>();
    for (const orderOption of this.orderOptions) {
      for (const pnoo of (orderOption.printNodeOrderOptions || [])) {
        for (const pkg of (pnoo.shippingPackages || [])) {
          for (const opt of (pkg.shippingOptions || [])) {
            if (!opt || !opt.carrier || !opt.serviceLevel) { continue; }
            const k = pairKey(opt.carrier, opt.serviceLevel);
            if (!pairSet.has(k)) { pairSet.set(k, { carrier: opt.carrier, serviceLevel: opt.serviceLevel }); }
          }
        }
      }
    }
    // Step 2/3 — for each pair, pick the cheapest eligible OrderOption.
    const choices: UnifiedShippingChoice[] = [];
    for (const { carrier, serviceLevel } of Array.from(pairSet.values())) {
      let best: {
        orderOption: OrderOption;
        shipping: number;
        packing: number;
        // Slowest-package aggregation across the best layout. Kept
        // per-candidate so if a slower layout ties or wins on price we
        // report *its* transit, not a stale one from a different option.
        deliveryDaysMin: number | null;
        deliveryDaysMax: number | null;
      } | null = null;
      for (const orderOption of this.orderOptions) {
        if (!orderOption.printNodeOrderOptions || orderOption.printNodeOrderOptions.length === 0) { continue; }
        let shippingSum = 0;
        let packingSum = 0;
        let anyPackage = false;
        let allEligible = true;
        // Aggregate transit-day range across every physical box in this
        // layout: whole-shipment transit is dictated by the slowest
        // package (`max` on both min and max). `anyRangeMissing` flips
        // to true if the server omitted a range for even one package —
        // in that case we surface `null` rather than a partial estimate.
        let rangeMin = 0;
        let rangeMax = 0;
        let anyRangeMissing = false;
        for (const pnoo of orderOption.printNodeOrderOptions) {
          if (!pnoo.shippingPackages || pnoo.shippingPackages.length === 0) {
            // Layout still computing packages — can't score it yet.
            allEligible = false; break;
          }
          for (const pkg of pnoo.shippingPackages) {
            anyPackage = true;
            const match = (pkg.shippingOptions || []).find(o => o && o.carrier === carrier && o.serviceLevel === serviceLevel);
            if (!match) {
              // At least one physical box in this layout has no rate
              // for this (carrier, serviceLevel) — the whole order
              // can't ship via this pair on this layout.
              allEligible = false; break;
            }
            shippingSum += match.cost * (pkg.quantity || 1);
            const range = match.getDeliveryDaysRange();
            if (!range) {
              anyRangeMissing = true;
            } else {
              if (range.min > rangeMin) { rangeMin = range.min; }
              if (range.max > rangeMax) { rangeMax = range.max; }
            }
          }
          if (!allEligible) { break; }
          // Packing cost is a per-PNOO helper (see
          // `PrintNodeOrderOption.getTotalPackingCost`, which handles
          // both server-provided and per-book fallback pricing).
          packingSum += pnoo.getTotalPackingCost();
        }
        if (!allEligible || !anyPackage) { continue; }
        const total = shippingSum + packingSum;
        if (!best || total < best.shipping + best.packing) {
          best = {
            orderOption,
            shipping: shippingSum,
            packing: packingSum,
            deliveryDaysMin: anyRangeMissing ? null : rangeMin,
            deliveryDaysMax: anyRangeMissing ? null : rangeMax
          };
        }
      }
      if (best) {
        choices.push({
          carrier,
          serviceLevel,
          shippingCost: best.shipping,
          packingCost: best.packing,
          totalCost: best.shipping + best.packing,
          orderOption: best.orderOption,
          packageSummary: this.summarizePackages(best.orderOption),
          deliveryDaysMin: best.deliveryDaysMin != null ? best.deliveryDaysMin : undefined,
          deliveryDaysMax: best.deliveryDaysMax != null ? best.deliveryDaysMax : undefined
        });
      }
    }
    // Step 4 — cheapest first.
    choices.sort((a, b) => a.totalCost - b.totalCost);
    return choices;
  }

  /**
   * Human-readable one-line summary of the physical package layout
   * for a given OrderOption — used by the shipping-section UI so the
   * user can see what boxes back each carrier+service option.
   *
   * Rules (2026-07-09 refresh, updated pass 3):
   *  - Any package whose depth is ≤ 1.5 in is labelled "envelope"
   *    (Shippo/EasyPost flat-mailer definition). Everything else is
   *    "box".
   *  - **Envelopes are labelled by kind only, without dimensions.**
   *    Envelope sizes are noise for the user (flat mailers all look
   *    the same and vary only slightly by depth), so a layout of
   *    four flat mailers renders as "4 envelopes" rather than
   *    "4 × 10×7×1in envelopes". Boxes still show dimensions
   *    because those genuinely help the user picture the shipment.
   *  - Box dimensions are rounded to the nearest half-inch so the
   *    display stays compact (`10.5×8×1.5in`, not `10.4375×8.02×1.5in`).
   *  - Identical `(kind, rounded-dimensions)` tuples are collapsed:
   *    two 12×9×3in boxes become "2 × 12×9×3in boxes"; all envelopes
   *    of any size collapse into a single "N envelope(s)" entry
   *    because dimensions are dropped from the key for envelopes.
   *
   * Ordering keeps first-seen order (which is the packer's order),
   * so a mixed 3-piece layout renders as "2 × 12×9×3in boxes, 1
   * envelope" rather than being re-sorted.
   */
  private summarizePackages(orderOption: OrderOption): string {
    if (!orderOption || !orderOption.printNodeOrderOptions) { return ''; }
    // Ordered map so the display keeps the packer's original order.
    // For envelopes the key is just `envelope|` so every envelope of
    // any size collapses into one row; boxes still key on
    // dimensions so different-sized boxes stay distinct.
    const seen = new Map<string, { count: number; kind: string; dims: string }>();
    for (const pnoo of orderOption.printNodeOrderOptions) {
      for (const pkg of (pnoo.shippingPackages || [])) {
        const perInstanceCount = (pkg.quantity && pkg.quantity > 0) ? pkg.quantity : 1;
        const kind = (pkg.depth != null && pkg.depth <= 1.5) ? 'envelope' : 'box';
        let dims = '';
        if (kind === 'box') {
          const dHalf = this.roundToHalfInch(pkg.depth);
          const wHalf = this.roundToHalfInch(pkg.width);
          const hHalf = this.roundToHalfInch(pkg.height);
          dims = `${dHalf}\u00d7${wHalf}\u00d7${hHalf}in`;
        }
        const key = `${kind}|${dims}`;
        const existing = seen.get(key);
        if (existing) {
          existing.count += perInstanceCount;
        } else {
          seen.set(key, { count: perInstanceCount, kind, dims });
        }
      }
    }
    if (seen.size === 0) { return ''; }
    const parts: string[] = [];
    for (const { count, kind, dims } of Array.from(seen.values())) {
      // Pluralise the kind noun — envelopes take `-s`, boxes take
      // `-es`. Dimensions only appear on box entries; envelope
      // entries display as `N envelope(s)`.
      const plural = kind === 'envelope' ? 'envelopes' : 'boxes';
      const noun = count > 1 ? plural : kind;
      if (kind === 'envelope') {
        // No dimensions for envelopes — user doesn't need to
        // picture the flat-mailer thickness.
        parts.push(count > 1 ? `${count} \u00d7 ${noun}` : noun);
      } else {
        parts.push(count > 1 ? `${count} \u00d7 ${dims} ${noun}` : `${dims} ${noun}`);
      }
    }
    return parts.join(', ');
  }

  /**
   * Round `value` to the nearest 0.5 (inch). `NaN`/`null`/`undefined`
   * safe — returns 0 in those cases so the caller doesn't crash on a
   * malformed package dimension. Uses `Math.round(v * 2) / 2` which
   * favours even halves on midpoint ties (standard banker's-round
   * behaviour isn't observably different at half-inch resolution).
   */
  private roundToHalfInch(value: number): number {
    if (value == null || isNaN(value)) { return 0; }
    return Math.round(value * 2) / 2;
  }

  addProductItem(product: Product, additionalQuantity: number): Item {
    const item: Item = new ProductItem( product );
    for ( const i of this.items ) {
      if ( i.getId() === product.getId() ) {
        const beforeQuantity = i.quantity;
        i.quantity += additionalQuantity;
        if ( beforeQuantity !== i.quantity && (beforeQuantity === 0 || i.quantity === 0 )) {
          this.computeItemsInCart();
        }
        return i;
      }
    }
    item.quantity += additionalQuantity;
    this.items.push(item);
    return item;
  }
    addDraftItem(draft: Draft, additionalQuantity: number): Item {
      const item: Item = new DraftItem( draft );
      for ( const i of this.items ) {
        if ( i.getId() === draft.getId() ) {
          const beforeQuantity = i.quantity;
          i.quantity += additionalQuantity;
          if ( beforeQuantity !== i.quantity && (beforeQuantity === 0 || i.quantity === 0 )) {
            this.computeItemsInCart();
          }
          return i;
        }
      }
      item.quantity += additionalQuantity;
      this.items.push(item);
      this.computeItemsInCart();
      return item;
  }

  remove(item: Item) {
    const index = this.items.indexOf(item);
    if ( index !== -1 ) {
      this.items.splice(index, 1);
    }
  }

  setBillingAddressTheSameAsShipping() {
    this.firstNamePayment = this.address.firstname;
    this.lastNamePayment = this.address.lastname;
    this.address1Payment = this.address.address1;
    this.address2Payment = this.address.address2;
    this.cityPayment = this.address.city;
    this.statePayment = this.address.state;
    this.postalCodePayment = this.address.postalcode;
    this.countryPayment = this.address.country;
  }

  setShippingAddressTheSameAsBilling() {
    this.address.firstname = this.firstNamePayment;
    this.address.lastname = this.lastNamePayment;
    this.address.address1 = this.address1Payment;
    this.address.address2 = this.address2Payment;
    this.address.city = this.cityPayment;
    this.address.state = this.statePayment;
    this.address.postalcode = this.postalCodePayment;
    this.address.country = this.countryPayment;
  }

  isPaymentInfoReady(): boolean {
    if ( this.chargeProfile ) {
      // A selected ChargeProfile is only "ready" if its stored expiration is
      // still in the future. The token is otherwise opaque to the client.
      if (typeof this.chargeProfile.isNotExpired === 'function' && !this.chargeProfile.isNotExpired()) {
        this.paymentErrorMessage = LanguagePipe.get('Selected card has expired', 'compressSpaces');
        this.paymentInfoReady = false;
        return false;
      }
      return true;
    }
    if ( !this.expMonthYear ) {
      this.paymentErrorMessage = LanguagePipe.get('NeedExpirationMonthAndYear', 'compressSpaces');
      this.paymentInfoReady = false;
      return false;
    }

    if ( !this.cardNumberWithSpaces ) {
      this.paymentErrorMessage = LanguagePipe.get('NeedCardNumber', 'compressSpaces');
      this.paymentInfoReady = false;
      return false;
    }
    this.cardNumber = this.cardNumberWithSpaces.split(' ').join('');

    this.expMonthYear.split(' ').join('');

    this.expMonth = Number(this.expMonthYear.substring(0, 2));
    this.expYear = Number(this.expMonthYear.substring(5));
    if ( this.expYear < 100 ) {
      this.expYear += 2000;
    }

    if (!this.isNotUndefinedAndMinimumLength(this.firstNamePayment, 1, 32)) {
      this.paymentErrorMessage = LanguagePipe.get('MustHaveFirstName', 'compressSpaces');
      this.paymentInfoReady = false;
      return false;
    }
    if (!this.isNotUndefinedAndMinimumLength(this.lastNamePayment, 2, 32)) {
      this.paymentErrorMessage = LanguagePipe.get('MustHaveLastName', 'compressSpaces');
      this.paymentInfoReady = false;
      return false;
    }
    if (!this.isNotUndefinedAndMinimumLength(this.address1Payment, 1, 32)) {
      this.paymentErrorMessage = LanguagePipe.get('MustHaveStreetAddress', 'compressSpaces');
      this.paymentInfoReady = false;
      return false;
    }
    // if (!this.isNotUndefinedAndMinimumLength(this.address2Payment, 0, 32)) {
    //   this.paymentErrorMessage = LanguagePipe.get('MustHaveStreetAddress', 'compressSpaces');
    //   this.paymentInfoReady = false;
    //   return false;
    // }
    if (!this.isNotUndefinedAndMinimumLength(this.cityPayment, 2, 32)) {
      this.paymentErrorMessage = LanguagePipe.get('MustHaveCity', 'compressSpaces');
      this.paymentInfoReady = false;
      return false;
    }
    if (!this.isNotUndefinedAndMinimumLength(this.statePayment, 2, 2)) {
      this.paymentErrorMessage = LanguagePipe.get('MustHaveState', 'compressSpaces');
      this.paymentInfoReady = false;
      return false;
    }
    if (!this.isNotUndefinedAndMinimumLength(this.postalCodePayment, 5, 16)) {
      this.paymentErrorMessage = LanguagePipe.get('MustHavePostalCode', 'compressSpaces');
      this.paymentInfoReady = false;
      return false;
    }
    if (!this.isNotUndefinedAndMinimumLength(this.countryPayment, 2, 2)) {
      this.paymentErrorMessage = LanguagePipe.get('MustHaveCountry', 'compressSpaces');
      this.paymentInfoReady = false;
      return false;
    }
    if (!this.isNotUndefinedAndMinimumLength(this.csc, 3, 4)) {
      this.paymentErrorMessage = LanguagePipe.get('CSCCodeMustBeThreeOrFourCharacters', 'compressSpaces');
      this.paymentInfoReady = false;
      return false;
    }
    if (!this.isNotUndefinedAndValueRange(this.expMonth, 1, 12)) {
      this.paymentErrorMessage = LanguagePipe.get('MonthMustBeBetween1And12', 'compressSpaces');
      this.paymentInfoReady = false;
      return false;
    }
    if (!this.isNotUndefinedAndValueRange(this.expYear, 2024, 2038)) {
      this.paymentErrorMessage = LanguagePipe.get('YearMustBeBetween2024And2038', 'compressSpaces');
      this.paymentInfoReady = false;
      return false;
    }
    if (!this.isNotUndefinedAndMinimumLength(this.cardNumber, 16, 19)) {
      this.paymentErrorMessage = LanguagePipe.get('CardNumberMustBeBetween16And19Numbers', 'compressSpaces');
      this.paymentInfoReady = false;
      return false;
    }
    if (!this.validCardNumberWithCardNumber(this.cardNumber)) {
      this.paymentErrorMessage = LanguagePipe.get('CardNumberMustHaveAValildCheckSum', 'compressSpaces');
      this.paymentInfoReady = false;
      return false;
    }
    this.paymentErrorMessage = "";
    this.paymentInfoReady = true;
    return true;
  }

  validCardNumber(): boolean {
    this.cardNumber = this.cardNumberWithSpaces.split(' ').join('');
    if ( this.validCardNumberWithCardNumber( this.cardNumber ) ) {
      return true;
    }
    return false;
  }

  validCardNumberWithCardNumber(number: string) {
    const regex = new RegExp("^[0-9]{16,19}$");
    if (!regex.test(number)) {
      return false;
    }
    return this.luhnCheck(number);
  }

  luhnCheck(val: string) {
    let sum = 0;
    for (let i = 0; i < val.length; i++) {
      let intVal = Number((val.substr(i, 1)));
      if (i % 2 === 0) {
        intVal *= 2;
        if (intVal > 9) {
          intVal = 1 + (intVal % 10);
        }
      }
      sum += intVal;
    }
    return (sum % 10) === 0;
  }
  isShippingReady(): boolean {
    this.shippingReady = false;
    return false;
  }

  /**
   * True once every per-package shipping rate has landed — the
   * shipping tree is fully computed and downstream consumers (sales
   * tax, order totals) can trust `shippingCost` /
   * `packingAndPackagingCost` to be their final values.
   *
   * Distinct from `shippingStatus === 'READY'`, which flips as soon
   * as the FIRST per-facility `getPackages` response lands (long
   * before per-package rate calls resolve). Firing sales-tax on the
   * early `READY` used to send `shippingCost=0` / `packingCost=0`
   * to the tax service and cache the wrong figure (bug 2026-07-20).
   *
   * Mirrors the `isShippingComputing` inverse check in
   * `CartShippingSectionComponent` — kept on the model so any consumer
   * (tax, place-order, summary) can gate on the same criterion
   * without duplicating the loop.
   */
  isShippingFullyComputed(): boolean {
    if (this.shippingStatus !== 'READY') { return false; }
    for (const oo of (this.orderOptions || [])) {
      for (const pnoo of (oo.printNodeOrderOptions || [])) {
        if (!pnoo.shippingPackages || pnoo.shippingPackages.length === 0) { return false; }
        for (const pkg of pnoo.shippingPackages) {
          const cs = (pkg as any).computationStatus;
          if (cs === 'COMPUTING' || cs === 'DIRTY' || cs === 'INITIAL') { return false; }
        }
      }
    }
    return true;
  }

  /**
   * Narrower relative of {@link isShippingFullyComputed}: true when
   * every per-package rate for the CURRENTLY-SELECTED OrderOption has
   * landed. Used by `cart-page.maybeComputeSalesTax` — sales tax only
   * needs the selected layout's shipping/packing costs to be final,
   * and gating on the whole tree meant a stuck / errored rate call on
   * some OTHER carrier's layout blocked tax computation forever
   * (`PrintNodeOrderOption.compute`'s error branch is a no-op today —
   * packages whose `getShippingOptions` returned a non-SUCCESS status
   * stay `'COMPUTING'` permanently, poisoning the whole-tree gate).
   *
   * Returns false when nothing is selected yet — that case is handled
   * by the separate `!selectedOrderOption` guard in
   * `maybeComputeSalesTax`.
   */
  isSelectedShippingFullyComputed(): boolean {
    if (this.shippingStatus !== 'READY') { return false; }
    if (!this.selectedOrderOption) { return false; }
    const oo = this.selectedOrderOption;
    for (const pnoo of (oo.printNodeOrderOptions || [])) {
      if (!pnoo.shippingPackages || pnoo.shippingPackages.length === 0) { return false; }
      for (const pkg of pnoo.shippingPackages) {
        const cs = (pkg as any).computationStatus;
        if (cs === 'COMPUTING' || cs === 'DIRTY' || cs === 'INITIAL') { return false; }
        // Also require the selected (carrier, serviceLevel) to have
        // an actual rate row on this package — a package can be
        // `COMPLETE` for one carrier while lacking a quote for
        // another, and the selected pair is what shippingCost /
        // packingAndPackagingCost were derived from.
        const carrier = this.selectedCarrier;
        const svc = this.selectedServiceLevel;
        if (carrier && svc) {
          const opts = (pkg as any).shippingOptions || [];
          const hasMatch = opts.some((o: any) => o && o.carrier === carrier && o.serviceLevel === svc);
          if (!hasMatch) { return false; }
        }
      }
    }
    return true;
  }

  /**
   * Fires (once per transition) when every per-package shipping rate
   * has landed and {@link isShippingFullyComputed} flips from `false`
   * → `true`. Consumers (cart-page's `maybeComputeSalesTax`) subscribe
   * so downstream work that was previously gated on
   * `isShippingFullyComputed()` finally runs when the last package's
   * `getShippingOptions` response arrives.
   *
   * Motivating bug (2026-07-22): when a shipping choice is
   * auto-selected (organization default matched the first quote back),
   * `shippingChoiceAutoSelected` fires immediately — but per-package
   * rate calls are still in flight. `maybeComputeSalesTax` correctly
   * refuses to run against the incomplete tree, but nothing later
   * re-invoked it, so sales tax never computed and the order never
   * advanced to "ready to place". Manual selection worked because the
   * user could only click a radio after all rates had already landed.
   * This notifier gives the parent a reliable "now it's safe" signal.
   */
  shippingFullyComputedNotifier: Subject<void> = new Subject<void>();
  private lastShippingFullyComputed: boolean = false;

  /**
   * Fires whenever {@link setSelectedShippingChoice} runs — for both
   * a manual user pick and the deferred-microtask auto-select. Paired
   * with {@link shippingFullyComputedNotifier} so
   * `cart-page.maybeComputeSalesTax` fires again regardless of which
   * of the two prerequisites (selection made / rates fully in)
   * settles last.
   */
  selectedShippingChoiceNotifier: Subject<void> = new Subject<void>();

  /** Fire the notifier when computation just transitioned to fully-computed. */
  private emitShippingFullyComputedIfNewlyTrue(): void {
    const now = this.isShippingFullyComputed();
    if (now && !this.lastShippingFullyComputed) {
      this.lastShippingFullyComputed = true;
      try { this.shippingFullyComputedNotifier.next(); } catch (e) { console.error('shippingFullyComputedNotifier emit failed', e); }
    } else if (!now && this.lastShippingFullyComputed) {
      // Reset so the next COMPUTING → COMPLETE transition re-fires.
      this.lastShippingFullyComputed = false;
    }
  }

  isAddressReady(): boolean {
    if (!this.isNotUndefinedAndMinimumLength(this.address.firstname, 1, 32)) {
      this.addressReady = false;
      return false;
    }
    if (!this.isNotUndefinedAndMinimumLength(this.address.lastname, 2, 32)) {
        this.addressReady = false;
      return false;
    }
    if (!this.isNotUndefinedAndMinimumLength(this.address.address1, 1, 32)) {
        this.addressReady = false;
      return false;
    }
    if (!this.isNotUndefinedAndMinimumLength(this.address.address2, 0, 32)) {
        this.addressReady = false;
      return false;
    }
    if (!this.isNotUndefinedAndMinimumLength(this.address.city, 2, 32)) {
        this.addressReady = false;
      return false;
    }
    if (!this.isNotUndefinedAndMinimumLength(this.address.state, 2, 2)) {
        this.addressReady = false;
      return false;
    }
    if (!this.isNotUndefinedAndMinimumLength(this.address.postalcode, 5, 16)) {
        this.addressReady = false;
      return false;
    }
    if (!this.isNotUndefinedAndMinimumLength(this.address.country, 2, 2)) {
        this.addressReady = false;
      return false;
    }
    if (!this.isNotUndefinedAndMinimumLength(this.address.email, 7, 64)) {
        this.addressReady = false;
      return false;
    }
    // if (!this.isNotUndefinedAndMinimumLength(this.emailConfirmation, 7, 64)) {
    //   this.addressReady = false;
    //   return false;
    // }
    // if ( this.email !== this.emailConfirmation ) {
    //  this.addressReady = false;
    //   return false;
    // }
    if ( this.addressValidationStatus !== 'VALIDATED' && this.addressValidationStatus !== 'MANUAL_OVERRIDE' ) {
        this.addressReady = false;
      return false;
    }
    this.addressReady = true;
    return true;
  }

  isNotUndefinedAndValueRange(value: number, minValue: number, maxValue: number): boolean {
    if (value === undefined || value === null) {
      return false;
    }
    if ( value < minValue ) {
      return false;
    }
    if ( value > maxValue ) {
      return false;
    }
    return true;
  }
  isNotUndefinedAndMinimumLength(value: string, minLength: number, maxLength: number): boolean {
    if (value === undefined || value === null) {
      return false;
    }
    if ( value.length >= minLength ) {
      return true;
    }
    return false;
  }

  compute() {
    // Refresh `nonZeroItems` up-front. Callers used to be responsible
    // for calling `setNonZeroItems()` before `compute()` (proceedToCheckout
    // still does), but the 2026-07-08 quantity-triggered debounced
    // recompute path fires `compute()` directly from a `setTimeout`
    // callback with no chance to re-snapshot first. Without this line,
    // a quantity change from 0→>0 (or the reverse) leaves the newly-
    // included / excluded item out of the recompute — no shipping
    // request goes out for the fresh item set. Idempotent: calling it
    // twice per user action is cheap and always correct.
    this.setNonZeroItems();
    this.orderOptions = [];
    this.shippingStatus = 'COMPUTING';
    this.shippingErrorMessage = '';
    this.shippingAddressFingerprint = this.computeAddressFingerprint(this.address);
    while ( this.orderOptions.length > 0 ) {
      this.orderOptions.pop();
    }

    // Nothing to ship. Leave the cart in PENDING rather than
    // COMPUTING — 'COMPUTING' drives the checkout's "Getting
    // shipping options" spinner, and with no items nothing will
    // ever arrive to clear it.
    if ( this.nonZeroItems.length === 0 ) {
      this.shippingStatus = 'PENDING';
      return;
    }

    // Overall weight
    let totalWeight: number = 0;

    // All quantities 20 and over or all under 20
    const over20 = [];
    const under20 = [];

    // All Inventory items or all not inventory items or a mixture
    const inventoryItemFacilities: any = {};
    const hasInventoryItems = [];
    const noInventoryItems = [];

    // All the same binding type or a mixture
    const bindingItems: any = {};
    const paperTypeItems: any = {};

    const productionFacilities: any = {};

    for ( const item of this.nonZeroItems) {
      totalWeight += item.getWeight() * item.getQuantity();

      // Determine items with with over and under 20 quantity
      if ( item.getQuantity() >= 20 ) {
        over20.push(item);
      } else {
        under20.push(item);
      }

      // Determine facilities that can fulfill the whole order
      for ( const facilityId of ['1', '7']) {
        if ( this.canFacilityProduce(Number(facilityId), item.getBindingType(), item.quantity, item.getInteriorType(), item.getLaminateType(), item.getPages(), item.getPaperType()) ) {
          if ( !productionFacilities[facilityId] ) {
            productionFacilities[facilityId] = [];
          }
          productionFacilities[facilityId].push(item);
        }
      }

      // Determine items with inventory and have enough to fulfill
      if ( item instanceof ProductItem ) {
        const base = item.getBase() as Product;
        if ( base.facilityInventoryProducts && base.facilityInventoryProducts.length > 0 ) {
          let notFound = true;
          for ( const facilityInventoryProduct of base.facilityInventoryProducts ) {
            if ( facilityInventoryProduct.quantityOnHand - facilityInventoryProduct.quantityCommited >= item.getQuantity() ) {
              // Enough to fulfill this item
              if ( !inventoryItemFacilities['' + facilityInventoryProduct.printnode_ID] ) {
                inventoryItemFacilities['' + facilityInventoryProduct.printnode_ID] = [];
              }
              inventoryItemFacilities['' + facilityInventoryProduct.printnode_ID].push(item);
              notFound = false;
            }
          }
          if ( notFound ) {
            noInventoryItems.push(item);
          } else {
            hasInventoryItems.push(item);
          }

        }
      } else {
        if ( item instanceof DraftItem ) {
          noInventoryItems.push(item);
          const base = item.getBase() as Draft;
        }
      }

      // Determine items with different binding
      const binding = item.getBindingType();
      if ( !bindingItems[binding] ) {
        bindingItems[binding] = [];
      }
      bindingItems[binding].push(item);

      // Determine items with different binding
      const paperType = '' + item.getPaperType();
      if ( !paperTypeItems[paperType] ) {
        paperTypeItems[paperType] = [];
      }
      paperTypeItems[paperType].push(item);
    }

    // For the pure options
    // All over 20 that can all be made at the most geographically advantageous facility
    const allOver20 = under20.length === 0 ? false : true;

    // Only one title
    const onlyOneTitle = this.nonZeroItems.length === 1 ? true : false;

    let geographicallyAdvantageousFacilities = [7, 1];
    if ( this.address.country === 'US' || this.address.country === 'USA') {
      const firstDigitOfPostalCode = this.address.postalcode.length > 0 ? this.address.postalcode.substring(0, 1) : '7';
      // Per-prefix mapping kept verbose for readability and future per-prefix nuance.
      // ZIP prefixes 0-7 favor facility 7 (Houston, TX); 8-9 favor facility 1 (Phoenix, AZ).
      switch (firstDigitOfPostalCode) {
        case '0': geographicallyAdvantageousFacilities = [7, 1]; break;
        case '1': geographicallyAdvantageousFacilities = [7, 1]; break;
        case '2': geographicallyAdvantageousFacilities = [7, 1]; break;
        case '3': geographicallyAdvantageousFacilities = [7, 1]; break;
        case '4': geographicallyAdvantageousFacilities = [7, 1]; break;
        case '5': geographicallyAdvantageousFacilities = [7, 1]; break;
        case '6': geographicallyAdvantageousFacilities = [7, 1]; break;
        case '7': geographicallyAdvantageousFacilities = [7, 1]; break;
        case '8': geographicallyAdvantageousFacilities = [1, 7]; break;
        case '9': geographicallyAdvantageousFacilities = [1, 7]; break;
        default:  geographicallyAdvantageousFacilities = [7, 1]; break;
      }
    }

    // ── Facility selection ───────────────────────────────────────────
    // Everything above only tallied capabilities. This block turns those
    // tallies into the actual OrderOption tree, and it is the ONLY thing
    // that causes shipping traffic: each `PrintNodeOrderOption.compute()`
    // fires `packageandshippingcomputation/packages` followed by a
    // per-package `.../shippingoptions` call. If no OrderOption is
    // created, no request is ever made, `shippingStatus` stays
    // 'COMPUTING', and the checkout spins on "Getting shipping options"
    // forever with an empty network log.
    //
    // That is exactly what the previous implementation did as soon as the
    // geographically-preferred facility could not produce the WHOLE order
    // (e.g. a COIL-bound title with facility 7 preferred, a title over
    // 1200pp, or a legacy SADDLE_STITCH title — that binding is
    // discontinued, so no facility takes it; see
    // `src/model/BindingType.ts`). It had three defects:
    //   1. `for ... in` was used to walk ARRAYS, so it compared array
    //      INDICES against facility IDs and never matched — the whole
    //      inventory-driven branch was dead code.
    //   2. the "second best facility" test read `....lenth` (typo →
    //      `undefined`), so it never fired; and when it did it built the
    //      PrintNodeOrderOption for `geo[0]` instead of the second-best
    //      facility it had just chosen.
    //   3. the final "must be split" branch was an empty comment block —
    //      no OrderOption, no request, no error, no way for the operator
    //      to know anything had gone wrong (bug report 2026-08-14).
    //
    // The rewrite below always produces an OrderOption when there are
    // items, splitting across facilities when no single facility can do
    // everything, and falls back to an explicit ERROR state instead of a
    // silent spinner if even that is impossible.

    // On-hand inventory only steers facility choice when the operator
    // actually wants inventory used. An Organization checking out with the
    // default PRINT_TO_ORDER inventory type expects fresh production, so
    // stock levels must not quietly reroute the order.
    const considerInventory = this.inventoryType !== 'PRINT_TO_ORDER';
    const itemsInStockAt = (facilityId: number): Item[] => {
      if ( !considerInventory ) { return []; }
      return inventoryItemFacilities['' + facilityId] || [];
    };
    const canFacilityHandleItem = (facilityId: number, item: Item): boolean => {
      if ( itemsInStockAt(facilityId).indexOf(item) >= 0 ) { return true; }
      // Reuse the production tally built above when it covers this
      // facility (it does for the standard 1 / 7 pair); fall back to a
      // direct capability check for anything outside that set.
      const producibleHere = productionFacilities['' + facilityId];
      if ( producibleHere ) { return producibleHere.indexOf(item) >= 0; }
      return this.canFacilityProduce(facilityId, item.getBindingType(), item.quantity, item.getInteriorType(), item.getLaminateType(), item.getPages(), item.getPaperType());
    };

    // 1. Best case — a single facility (nearest first) that can handle
    //    every item, either from stock or by producing it.
    let singleFacility = 0;
    for ( const facilityId of geographicallyAdvantageousFacilities ) {
      let handlesEverything = true;
      for ( const item of this.nonZeroItems ) {
        if ( !canFacilityHandleItem(facilityId, item) ) { handlesEverything = false; break; }
      }
      if ( handlesEverything ) { singleFacility = facilityId; break; }
    }

    if ( singleFacility ) {
      const orderOption = new OrderOption(this.address, 'ANY', 'ANY', this.packItemsInSeparateBoxes, this.useCaseQuantities, 'SINGLEFACILITY', this.packageAndShippingComputationService);
      const printNodeOrderOption = orderOption.addPrintNodeOrderOption(singleFacility);
      for ( const item of this.nonZeroItems ) {
        printNodeOrderOption.addItem(item);
      }
      this.addOrderOptionWithFlatRateAlternative(orderOption);
    } else {
      // 2. Split the order. Each item goes to the nearest facility that can
      //    handle it. Items no facility can handle are still assigned to the
      //    nearest facility so the customer gets a quote instead of an
      //    endless spinner, but they're called out so the discrepancy is
      //    visible rather than silent.
      const itemsByFacility: { [facilityId: number]: Item[] } = {};
      const unhandledItems: Item[] = [];
      for ( const item of this.nonZeroItems ) {
        let assignedFacility = 0;
        for ( const facilityId of geographicallyAdvantageousFacilities ) {
          if ( canFacilityHandleItem(facilityId, item) ) { assignedFacility = facilityId; break; }
        }
        if ( !assignedFacility ) {
          assignedFacility = geographicallyAdvantageousFacilities[0];
          unhandledItems.push(item);
        }
        if ( !itemsByFacility[assignedFacility] ) { itemsByFacility[assignedFacility] = []; }
        itemsByFacility[assignedFacility].push(item);
      }
      const facilitiesInUse = geographicallyAdvantageousFacilities.filter(facilityId => itemsByFacility[facilityId] && itemsByFacility[facilityId].length > 0);
      if ( facilitiesInUse.length > 0 ) {
        // One OrderOption carrying one PrintNodeOrderOption per facility.
        // `getUnifiedShippingChoices()` already aggregates across every
        // PNOO of an option, so a split shipment surfaces as a single
        // priced choice per (carrier, serviceLevel) whose cost covers all
        // facilities.
        const strategy = facilitiesInUse.length > 1 ? 'MULTIFACILITY' : 'SINGLEFACILITY';
        const orderOption = new OrderOption(this.address, 'ANY', 'ANY', this.packItemsInSeparateBoxes, this.useCaseQuantities, strategy, this.packageAndShippingComputationService);
        for ( const facilityId of facilitiesInUse ) {
          const printNodeOrderOption = orderOption.addPrintNodeOrderOption(facilityId);
          for ( const item of itemsByFacility[facilityId] ) {
            printNodeOrderOption.addItem(item);
          }
        }
        this.addOrderOptionWithFlatRateAlternative(orderOption);
      }
      if ( unhandledItems.length > 0 ) {
        const titles = unhandledItems.map(item => item.getTitle()).filter(title => !!title);
        // Separate "we don't make this any more" from "no facility can
        // currently take it". A legacy saddle-stitch title can still be
        // sitting in a customer's catalogue and be re-added to a cart, and
        // telling them it needs "special handling" would be misleading —
        // it can't be manufactured at all.
        const allDiscontinued = unhandledItems.every(item => isDiscontinuedBindingType(item.getBindingType()));
        console.warn('Cart.compute: no facility can manufacture these items; routing them to the nearest facility anyway', unhandledItems);
        this.shippingErrorMessage = allDiscontinued
          ? LanguagePipe.get('These items are no longer produced', 'compressSpaces')
          : LanguagePipe.get('Some items may need special handling to manufacture', 'compressSpaces');
        if ( titles.length > 0 ) {
          this.shippingErrorMessage += ': ' + titles.join(', ');
        }
      }
    }

    // Last line of defence. If we somehow produced nothing, say so loudly
    // instead of leaving the checkout spinning on 'COMPUTING' with no
    // network traffic and no explanation.
    if ( this.orderOptions.length === 0 ) {
      console.error('Cart.compute: no OrderOption could be built for the cart', { items: this.nonZeroItems.length, address: this.address });
      this.shippingStatus = 'ERROR';
      if ( !this.shippingErrorMessage ) {
        this.shippingErrorMessage = LanguagePipe.get('No facility can fulfill this order', 'compressSpaces');
      }
    }
  }

  /**
   * Push an OrderOption onto the cart and kick off its computation,
   * together with the USPS flat-rate variant of the same facility/item
   * layout. Every facility-selection branch funnels through here so the
   * flat-rate alternative can never be accidentally omitted (it was
   * duplicated inline in each branch before 2026-08-14) and so there is a
   * single place that starts shipping traffic.
   */
  private addOrderOptionWithFlatRateAlternative(orderOption: OrderOption): void {
    orderOption.compute(this);
    this.orderOptions.push(orderOption);
    const clonedOrderOption = orderOption.cloneWIthDifferentCarrierAndServiceLevel('USPS', 'PRIORITY_FLAT');
    clonedOrderOption.compute(this);
    this.orderOptions.push(clonedOrderOption);
  }

  checkComputationStatus(): string {
    if ( this.computationStatus === 'INITIAL') {
      return 'INITIAL';
    }
    if ( this.computationStatus === 'ERROR') {
      return 'ERROR';
    }
    let cs = '';
    this.orderOptions.forEach( orderOptions => {
      if ( orderOptions.computationStatus === 'ERROR') {
        this.computationStatus = 'ERROR';
        return this.computationStatus;
      }
      if ( orderOptions.computationStatus === 'COMPUTING' ) {
        cs = 'COMPUTING';
      }
    });
    if ( cs.length === 0) {
      this.computationStatus = 'COMPLETE';
    } else {
      this.computationStatus = 'COMPUTING';
    }
    // Notify any listeners the moment the per-package tree finished
    // computing. Gated so it only fires on the false→true transition
    // (see `emitShippingFullyComputedIfNewlyTrue` docs). Placed here
    // — the only path the per-package `getShippingOptions` subscribe
    // in `PrintNodeOrderOption.compute` calls after mutating a
    // package's `computationStatus` — so consumers are guaranteed to
    // hear about completion regardless of whether the user manually
    // selected a shipping choice or the picker auto-selected the
    // organization default.
    this.emitShippingFullyComputedIfNewlyTrue();
    return this.computationStatus;
  }

  canFacilityProduce(facilityId: number, bindingType: string, quantity: number, interiorType: string, laminationType: string, pages: number, paperType: number) {
    // Discontinued products first. This is NOT a facility limitation —
    // saddle-stitch books simply aren't produced any more (the
    // `BindingType` value survives only so historical orders keep
    // rendering; see `src/model/BindingType.ts`). Checking it ahead of
    // the per-facility switch means bringing a new facility online, or
    // relaxing an existing facility's rules, can never accidentally
    // re-enable something we no longer make.
    if ( isDiscontinuedBindingType(bindingType) ) {
      return false;
    }
    switch ( facilityId ) {
      case 1: {
        // if ( pages < 24 ) {
        //   return false;
        // }
        if ( pages > 1200 ) {
          return false;
        }
        if ( bindingType === 'PERFECT' && quantity < 20 && interiorType === 'GREY' ) {
          return false;
        }
        break;
      }
      case 7: {
        // if ( pages < 38 ) {
        //   return false;
        // }
        if ( pages > 1200 ) {
          return false;
        }
        if ( bindingType === 'COIL') {
          return false;
        }
        break;
      }
      default: {
        break;
      }
    }
    return true;
  }

  // getFacilities(): number[] {
  //   const facilities: number[] = [];
  //   for ( const orderOption of this.orderOptions) {
  //     for ( const printNodeOrderOption of orderOption.printNodeOrderOptions) {
  //       if ( facilities.indexOf(printNodeOrderOption.facilityId ) === -1 ) {
  //         facilities.push(printNodeOrderOption.facilityId);
  //       }
  //     }
  //   }
  //   return facilities;
  // }
  // getPackages(carrier: string, serviceLevel: string) {
  //
  // }
  //
  // getShippingOptions() {
  //
  // }
  verifyCosts(): boolean {
    return true;
  }

  verifyCostsAndShipping(): boolean {
    this.totalQuantity = 0;
    this.totalWeight = 0;
    this.totalCost = 0;

    for ( const item of this.items ) {
      if ( item.quantity > 0 ) {
        this.totalQuantity++;
        this.totalCost += item.cost;
        this.totalWeight += ( item.getWeight() * item.quantity );
      }
    }
    return this.verifyCosts();
  }

  verifyShipping(): boolean {
    return true;
  }

  clear() {
    this.userId = '';
    this.key = '';
    this.expeditedManufacturing = '';
    this.expeditedManufacturingCost = 0;
    this.shippingMethod = 'USPS_PRIORITY';
    this.shippingCost = 0;
    this.transcriptionCost = 0;
    this.itemsCost = 0;
    this.packingAndPackagingCost = 0;
    this.totalQuantity = 0;
    this.taxCost = 0;
    this.accountId = 0;
    this.verificationKey = '';
    this.address.firstname = '';
    this.address.lastname = '';
    this.address.address1 = '';
    this.address.address2 = '';
    this.address.company = '';
    this.address.city = '';
    this.address.state = '';
    this.address.postalcode = '';
    this.address.country = 'US';
    this.address.email = '';
    this.purchaseOrder = '';
    this.purchaseOrderPrivate = '';
    this.phone = '';
    this.phoneCountry = '';
    this.cardNumber = '';
    this.csc = '';
    this.expMonth = 12;
    this.expYear = 2020;
    this.firstNamePayment = '';
    this.lastNamePayment = '';
    this.address1Payment = '';
    this.address2Payment = '';
    this.cityPayment = '';
    this.statePayment = '';
    this.postalCodePayment = '';
    this.countryPayment = 'US';

    this.itemsInCart = 0;

    this.inventoryType = 'PRINT_TO_ORDER';
    this.payer = 'ORGANIZATION';

    this.orderOptions = [];
    this.packageCollections = {};

    this.addressValidationStatus = 'INITIAL';
    this.packItemsInSeparateBoxes = false;
    this.useCaseQuantities = false;

    // Reset workflow state.
    this.cartStage = 'CART';
    this.itemsStatus = 'EMPTY';
    this.addressStatus = 'PENDING';
    this.facilityStatus = 'PENDING';
    this.packagesStatus = 'PENDING';
    this.shippingStatus = 'PENDING';
    this.salesTaxStatus = 'PENDING';
    this.paymentStatus = 'PENDING';
    this.placeOrderStatus = 'IDLE';
    this.salesTaxResult = undefined;
    this.salesTaxSignature = '';
    this.salesTaxAddressFingerprint = '';
    this.facilityCandidates = [];
    this.facilitySignature = '';
    this.splitOrderRequired = false;
    this.rateShippingByFacility = {};
    this.rateShippingSignature = '';
    this.selectedShippingRateSignature = '';
    this.selectedPackageSetId = '';
    this.selectedCarrier = '';
    this.selectedServiceLevel = '';

    if ( this.items ) {
      for ( const item of this.items ) {
        item.quantity = 0;
        // Also zero out the derived per-item pricing state so any UI
        // that reads `item.cost` / `item.getUnitCost()` / signature
        // fields directly after a successful checkout shows a clean
        // slate. `item.quantity = 0` alone leaves stale positive
        // values on these fields because `computeCost` is only
        // invoked reactively on quantity changes / rate fetches; it
        // does not re-run here.
        item.cost = 0;
        item.costFacilityId = 0;
        item.productionRateSignature = '';
        item.previousQuantity = 0;
        // Deliberately keep `item.productionRates` +
        // `item.productionRatesStatus` populated — the rate cache
        // is still valid for the same product if the user
        // immediately adds it to a fresh cart, and re-fetching would
        // just recreate the same table.
      }
    }
  }

  getItemsInCart() {
    return this.itemsInCart;
  }

  computeItemsInCart() {
    let count = 0;
    for ( const item of this.items ) {
      if ( item.quantity > 0 ) {
        count++;
      }
    }
    this.itemsInCart = count;
  }
}

